import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import { navigate, graphql, Link } from 'gatsby';
import { Grid, Stack, Button, Divider, Container, Typography, Box, CssBaseline, Paper, IconButton } from '@mui/material';
import CancelIcon from "@mui/icons-material/Cancel";

import useLocation from '../hooks/useLocation';

import PromotionsCarousel from '../components/PromotionsCarousel';

import TicketPurchaseTabs from '../features/Tickets/components/ticket-purchase-tabs';
import { HomeShuttleReservation } from '../features/Shuttle/components/home-shuttle-reservation';
import HomeParkingReservation from '../features/Parking/components/home-parking-reservation';

import {
  MainContainer,
  ServiceFormContainer,
  ServiceCardsContainer,
  DocumentationCardsContainer,
} from '../features/Home/components/styles';
import Slogan from '../features/Home/components/Slogan';
import ServiceCard from '../features/Home/components/ServiceCard';
import CookiesModal from '../components/CookiesModal';
import DocOverviewCard from '../features/Home/components/DocOverviewCard';
import CampaignsCarousel from '../features/Home/components/CampaignsCarousel';

import { HomeWheelsReservation } from '../features/wheels/components/home-wheels-reservation';
import { Layout } from '../components/Layout';
import { useIsMobile } from '../hooks';
import Modal from '../components/Modal';
import useBundleRecommendationContext from '../context/BundleRecommendation/use-bundle-recommendation-context';
import { isBundleDisclaimerEnabled } from '../features/bundles/repository/bundles.repository.config';

const hashMaps = {
  '#parking': 'parking_service',
  '#shuttle': 'shuttle_service',
  '#wheels': 'wheels_service',
  '#tickets': 'tickets',
};

const serviceMaps = {
  parking_service: '#parking',
  shuttle_service: '#shuttle',
  wheels_service: '#wheels',
  tickets: '#tickets',
};

export default function Index({ firebase, uid, user }) {
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();
  const { location } = useLocation();
  const isMobile = useIsMobile();

  const { disclaimer } = useBundleRecommendationContext();

  let hash = '';
  if (typeof window != 'undefined') {
    hash = window.location.hash;
  }
  let defaultService = hashMaps[hash];
  if (!defaultService) defaultService = 'tickets';
  const [selectedService, setSelectedService] = useState(defaultService);
  const [campaignDisclaimer, setCampaignDisclaimer] = useState('');

  const getDisclaimer = async (campaign) => {
    const fetchResponse = await fetch(`${process.env.GATSBY_CMSV2_URL}/disclaimers`, { timeout: 1500 });
    const data = await fetchResponse.json();
    const disclaimerData = data.filter(currentDisclaimer => currentDisclaimer.name === `${campaign}` && currentDisclaimer.enabled && currentDisclaimer.forWeb)[0];
    return disclaimerData;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let campaign = urlParams.get('utm_campaign');
    
    if (isBundleDisclaimerEnabled()) {
      if (disclaimer && disclaimer.disclaimer) {
        campaign = disclaimer?.disclaimer;
      }
     
    }

    if (!campaign) {
      return;
    }

    console.log('Campaign configured', campaign);
    const p = async () => {
      try {
        const data = await getDisclaimer(campaign);
        let text = data.text_en;
        if (data[`text_${language}`]) {
          text = data[`text_${language}`];
        }
        setCampaignDisclaimer(text);
      } catch (e) {
        setCampaignDisclaimer('');
      }
    };
    p();
  }, [disclaimer]);

  const redirectToBuyTickets = () => {
    navigate('/buy-tickets-mobile');
  };

  const changeSelectedService = (service: string) => {
    setSelectedService(service);
    navigate(serviceMaps[service]);
  };

  const handleAddServiceClick = (service: string) => {
    // DEPRECATED: Travel Club is no longer supported, commenting out the code 
    //             until it is removed or replaced with a new feature.
    // if (service === 'travel_club_service') {
    //   return navigate('/travel-club');
    // }

    if (isMobile) {
      switch (service) {
        case 'parking_service':
          return navigate('/buy-parking-mobile');
        case 'shuttle_service':
          return navigate('/buy-shuttle-mobile');
        case 'wheels_service':
          return navigate('/buy-wheels-mobile');
        default:
          return;
      }
    }

    changeSelectedService(service);
  };

  const renderSelectedServiceComponent = () => {
    switch (selectedService) {
      case 'tickets':
        return (
          <>
            <Slogan />
            <Stack mt={3}>
              {isMobile && (
                <Button size="large" variant="contained" onClick={redirectToBuyTickets}>
                  {t('buy_tickets')}
                </Button>
              )}
              {!isMobile && <TicketPurchaseTabs />}
            </Stack>
          </>
        );

      case 'parking_service':
        return <HomeParkingReservation onBackClick={() => changeSelectedService('tickets')} />;

      case 'shuttle_service':
        return <HomeShuttleReservation onBackClick={() => changeSelectedService('tickets')} />;

      case 'wheels_service':
        return <HomeWheelsReservation onBackClick={() => changeSelectedService('tickets')} />;

      default:
        return <TicketPurchaseTabs />;
    }
  };
  

  return (
    <Layout firebase={firebase} uid={uid} user={user}>
      <Container maxWidth="xl" sx={{ bgcolor: '#FBFBFB' }}>
        <MainContainer>
          <Modal
            open={!!campaignDisclaimer}
            handleClose={() => { setCampaignDisclaimer('') }}
            sx={{ padding: 0 }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            maxWidth="600px"
          >
            <Paper sx={{ borderRadius: "8px", border: "none", maxHeight: "100vh", overflowY: "auto" }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 662,
                  height: "120px",
                  maxHeight: 256,

                  position: "relative",

                  backgroundSize: "cover",
                  backgroundColor: "#382656",
                  backgroundPosition: "center",

                  borderRadius: "8px 8px 0px 0px",
                }}
              >
                <Typography
                  variant="h5"
                  color="white"
                  fontWeight={700}
                  sx={{
                    width: "80%",
                    position: "absolute",
                    bottom: "24px",
                    left: "24px",
                  }}
                >
                  {t('admodal.title')}
                </Typography>
                <IconButton
                  onClick={() => { setCampaignDisclaimer('') }}
                  sx={{
                    padding: 0,
                    marginBottom: "8px",
                    position: "absolute",
                    backgroundColor: "white",
                    top: "24px",
                    right: "24px",
                    '&:hover': {
                      backgroundColor: "white"
                    }
                  }}
                >
                  <CancelIcon fontSize="large" sx={{ color: "#282032" }} />
                </IconButton>
              </Box>
              <Stack gap="16px" padding="32px" justifyContent="space-between">
                <div dangerouslySetInnerHTML={{ __html: campaignDisclaimer }}></div>
              </Stack>
            </Paper>
          </Modal>
          <ServiceFormContainer>{renderSelectedServiceComponent()}</ServiceFormContainer>
          <Grid item xs={12} md={6}>
            <Stack direction="column" gap="32px">
              <Stack direction="column" gap="16px">
                <img
                  src="/images/hero_image_240422.jpg"
                  alt="hero_image"
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                />
                <Divider textAlign="left">
                  <Typography
                    sx={{ padding: '8px 12px', background: '#F7F5F9', borderRadius: '16px' }}
                    fontSize={20}
                    color="primary"
                    variant="h5"
                    component="h3"
                    fontWeight={700}
                  >
                    {t('add_other_services')}
                  </Typography>
                </Divider>
                <ServiceCardsContainer>
                  {location && location.services.map((service_name: string) => (
                    <ServiceCard type={service_name} onAddServiceClick={() => handleAddServiceClick(service_name)} />
                  ))}
                  {false && <ServiceCard type="package_service" />}
                </ServiceCardsContainer>

                <Divider />
              </Stack>
              <Stack gap="24px">
                <CampaignsCarousel />
                <Stack spacing={1}>
                  <Typography variant="h2" color="#3F2B56" fontFamily="Gotham-Bold" fontSize="24px" align="left">
                    {t('requiredDocumentation.title')}
                  </Typography>
                  <Typography variant="body1" color="#333334" fontSize="16px" textAlign="left" lineHeight="20px" mt={2}>
                    {t('requiredDocumentation.description')}
                  </Typography>
                  <Typography
                    fontSize="16px"
                    color="#333334"
                    lineHeight="20px"
                    sx={{
                      a: {
                        color: '#EF60A3',
                        fontFamily: 'Gotham-Medium',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <Trans i18nKey="requiredDocumentation.requirements" components={[<Link to="/requirements" />]} />
                  </Typography>
                </Stack>
                <DocumentationCardsContainer>
                  <Box flex={1} maxWidth="240px">
                    <DocOverviewCard type={location?.documentation[0]} />
                  </Box>
                  <Box flex={1} maxWidth="240px">
                    <DocOverviewCard type={location?.documentation[1]} />
                  </Box>
                  <Box flex={1} maxWidth="240px">
                    <DocOverviewCard type={location?.documentation[2]} />
                  </Box>
                </DocumentationCardsContainer>
                <Typography variant="body1" color="#333334" fontSize="16px" textAlign="left" lineHeight="20px">
                  {t('requiredDocumentation.disclaimer')}
                </Typography>
              </Stack>
              {/* FIXME: When promotions are ready, uncomment the PromotionsCarousel component below */}
              {/* <Stack direction="column" gap="24px">
                <Stack direction="column" gap="8px">
                  <Typography color="primary" variant="h5" component="h3" fontWeight={700}>
                    {t('you_can_not_lose_this')}
                  </Typography>
                  <Typography color="secondary.light" mt={1}>
                    {t('you_can_not_lose_this_description')}
                  </Typography>
                </Stack>
              </Stack> 
              <PromotionsCarousel cardVariant="promotion" /> */}
            </Stack>
          </Grid>
        </MainContainer>
      </Container>
      {false && <CookiesModal />}
    </Layout >
  );
}

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(
      filter: { ns: { in: ["common", "home", "documentations", "checkout", "services"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
